import {createEnvironmentInjector, EnvironmentInjector, Injectable} from '@angular/core';
import {PreloadingStrategy, Route, RouterPreloader} from '@angular/router';
import {Observable, of} from 'rxjs';
import {RouteLike} from 'app/blocks/service/assistant-context/assistant-context';

/**
 * Loads the route config of the lazy modules an assistant reply links into (CU-14zefhy0dgu D3), so a reply link can be
 * checked against the real routes before it is shown as a link. HM loads its modules lazily with no preloading (RM
 * preloads all of them), so without this most reply links could not be checked.
 *
 * It uses Angular's own `RouterPreloader` with a strategy that loads only the requested routes, so the router's
 * config loader fills `_loadedRoutes` exactly as a navigation would and emits `RouteConfigLoadEnd` (the chat
 * re-checks its links on that event). Nothing else is preloaded; each route is requested once (a failed load stays
 * unloaded and its links stay plain text).
 */
@Injectable({providedIn: 'root'})
export class AssistantRoutePreloader {
    private readonly wanted = new Set<RouteLike>();
    private readonly requested = new WeakSet<RouteLike>();
    private preloader: RouterPreloader;
    private scheduled = false;

    constructor(private injector: EnvironmentInjector) {}

    /** Asks for these lazy routes to be loaded (batched into one preload pass). Never throws. */
    load(routes: RouteLike[]): void {
        try {
            (routes || []).forEach((r) => {
                if (r && !r._loadedRoutes && !this.requested.has(r)) {
                    this.requested.add(r);
                    this.wanted.add(r);
                }
            });
            if (!this.wanted.size || this.scheduled) {
                return;
            }
            this.scheduled = true;
            Promise.resolve().then(() => {
                this.scheduled = false;
                this.run();
            });
        } catch (e) {
            // links into that module stay plain text
        }
    }

    private run(): void {
        try {
            this.preloader = this.preloader || this.createPreloader();
            this.preloader.preload().subscribe({error: () => undefined});
        } catch (e) {
            this.wanted.clear();
        }
    }

    private createPreloader(): RouterPreloader {
        const wanted = this.wanted;
        const strategy: PreloadingStrategy = {
            preload(route: Route, load: () => Observable<any>): Observable<any> {
                if (!wanted.has(route as RouteLike)) {
                    return of(null);
                }
                wanted.delete(route as RouteLike);
                return load();
            }
        };
        // a child injector only to hand RouterPreloader this strategy; Router and its config loader still come from
        // the root, so loaded modules are shared with normal navigation. Kept for the app's lifetime (loaded modules
        // hang off it), never destroyed.
        const child = createEnvironmentInjector([{provide: PreloadingStrategy, useValue: strategy}, RouterPreloader], this.injector);
        return child.get(RouterPreloader);
    }
}
